import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface EmptyStateProps {
    icon?: LucideIcon
    title: string
    /** Say why it matters and what happens next. Never leave a void. */
    description: string
    action?: ReactNode
    secondaryAction?: ReactNode
    tone?: 'default' | 'danger'
    className?: string
}

export const EmptyState = ({
    icon: Icon,
    title,
    description,
    action,
    secondaryAction,
    tone = 'default',
    className
}: EmptyStateProps) => {
    const danger = tone === 'danger'

    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center gap-5 rounded-lg border border-dashed px-6 py-16 text-center',
                danger ? 'border-danger/40 bg-danger-soft' : 'border-line bg-surface',
                className
            )}
        >
            {Icon && (
                <span
                    className={cn(
                        'flex size-11 items-center justify-center rounded-full',
                        danger ? 'bg-danger/10 text-danger' : 'bg-surface-2 text-ink-subtle'
                    )}
                >
                    <Icon className='size-5' aria-hidden />
                </span>
            )}

            <div className='flex max-w-md flex-col gap-2'>
                <h3 className={cn('font-display text-h4', danger && 'text-danger')}>{title}</h3>
                <p className='text-small text-ink-muted text-pretty'>{description}</p>
            </div>

            {(action || secondaryAction) && (
                <div className='flex flex-wrap items-center justify-center gap-3'>
                    {action}
                    {secondaryAction}
                </div>
            )}
        </div>
    )
}

export default EmptyState
